import { z } from 'zod';
/**
 * App downloads (platform setting `core.apps`, portal page "Get the apps", route GET /api/v1/apps).
 * Platform admins give each Android app a download link. Only https links on an allowed host are accepted, so a
 * hijacked admin session cannot point staff at an arbitrary site.
 */
export const APP_KEYS = ['kiosk', 'companion'];
/** Android package of each app. */
export const APP_PACKAGES = { kiosk: 'uk.cloudsign.kiosk', companion: 'uk.cloudsign.app' };
/** A host is allowed when it equals one of these or is a sub-domain of one. */
export const APP_DOWNLOAD_HOSTS = ['expo.dev', 'cloudsign.uk'];
/** True for an https link (no user name or password) on an allowed host. */
export function isAllowedAppUrl(value) {
    let u;
    try {
        u = new URL(value);
    }
    catch {
        return false;
    }
    if (u.protocol !== 'https:' || u.username || u.password)
        return false;
    const host = u.hostname.toLowerCase();
    return APP_DOWNLOAD_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
}
export const AppDownloadSetting = z
    .object({
    /** Show this app on the "Get the apps" page. */
    enabled: z.boolean().default(false),
    /** https link on expo.dev or a cloudsign.uk host. Empty while the app is off. */
    url: z.string().trim().max(500).default(''),
    /** For example "0.1.0 (23 Oct 2026)". */
    version: z.string().trim().max(60).default(''),
})
    .superRefine((v, ctx) => {
    if (v.url !== '' && !isAllowedAppUrl(v.url))
        ctx.addIssue({ code: 'custom', path: ['url'], message: 'error.app_url_invalid' });
    else if (v.enabled && v.url === '')
        ctx.addIssue({ code: 'custom', path: ['url'], message: 'error.app_url_required' });
});
const OFF = { enabled: false, url: '', version: '' };
export const AppDownloadsSetting = z
    .object({
    kiosk: AppDownloadSetting.default(OFF),
    companion: AppDownloadSetting.default(OFF),
})
    .default({ kiosk: OFF, companion: OFF });
/** One entry of GET /api/v1/apps. */
export const AppDownload = z.object({
    key: z.enum(APP_KEYS),
    /** Android package name. */
    packageName: z.string(),
    url: z.string(),
    version: z.string(),
});
export const AppsResponse = z.object({ apps: z.array(AppDownload) });
/** GET /api/v1/support: the address behind the account-menu "Support" link (platform setting core.support.email). Empty = none. */
export const SupportResponse = z.object({ email: z.string() });
//# sourceMappingURL=apps.js.map